import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Box,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  LocalHospital as DiseaseIcon,
  MedicalServices as TreatmentIcon,
} from '@mui/icons-material';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { SEARCH_ALL } from '../graphql/operations';

interface SearchModalProps {
  open: boolean;
  onClose: () => void;
}

interface SearchResult {
  id: string;
  name: string;
  description?: string;
  short_description?: string;
  icd10_code?: string;
  treatment_type?: string;
  type: 'disease' | 'treatment';
  thumbnail_url?: string;
}

const SearchModal: React.FC<SearchModalProps> = ({ open, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Focus on search input when modal opens
  useEffect(() => {
    if (open) {
      // Use requestAnimationFrame to ensure modal is fully rendered
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (searchInputRef.current) {
            // Focus the actual input element inside the TextField
            const inputElement = searchInputRef.current.querySelector('input');
            if (inputElement) {
              inputElement.focus();
            } else {
              searchInputRef.current.focus();
            }
          }
        });
      });
    }
  }, [open]);

  // Reset search when modal closes
  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setDebouncedSearchTerm('');
    }
  }, [open]);

  const { data, loading } = useQuery(SEARCH_ALL, {
    variables: { query: debouncedSearchTerm, limit: 15 },
    skip: !debouncedSearchTerm || debouncedSearchTerm.length < 2,
  });

  const results: SearchResult[] = [];

  if (data?.diseases?.items) {
    results.push(
      ...data.diseases.items.map((disease: any) => ({
        id: disease.id,
        name: disease.name,
        description: disease.short_description,
        icd10_code: disease.icd10_code,
        type: 'disease' as const,
        thumbnail_url: disease.thumbnail_url,
      }))
    );
  }

  if (data?.treatments?.items) {
    results.push(
      ...data.treatments.items.map((treatment: any) => ({
        id: treatment.id,
        name: treatment.name,
        description: treatment.description,
        treatment_type: treatment.treatment_type,
        type: 'treatment' as const,
      }))
    );
  }

  const handleResultClick = (result: SearchResult) => {
    if (result.type === 'disease') {
      navigate(`/diseases/${result.id}`);
    } else {
      navigate('/treatments'); // Navigate to treatments page, could be enhanced to show specific treatment
    }
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  const renderResultItem = (result: SearchResult, index: number) => (
    <ListItem key={`${result.type}-${result.id}`} disablePadding>
      <ListItemButton onClick={() => handleResultClick(result)} sx={{ py: 1.5 }}>
        <ListItemAvatar>
          <Avatar
            sx={{
              bgcolor: result.type === 'disease' ? 'primary.main' : 'secondary.main',
              width: 40,
              height: 40,
            }}
            src={result.thumbnail_url}
          >
            {result.thumbnail_url ? null : result.type === 'disease' ? (
              <DiseaseIcon />
            ) : (
              <TreatmentIcon />
            )}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                {result.name}
              </Typography>
              <Chip
                label={result.type === 'disease' ? 'Disease' : 'Treatment'}
                size="small"
                color={result.type === 'disease' ? 'primary' : 'secondary'}
                variant="outlined"
              />
              {result.icd10_code && (
                <Chip
                  label={result.icd10_code}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              )}
              {result.treatment_type && (
                <Chip
                  label={result.treatment_type}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.75rem' }}
                />
              )}
            </Box>
          }
          secondary={
            result.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  mt: 0.5,
                }}
              >
                {result.description}
              </Typography>
            )
          }
        />
      </ListItemButton>
    </ListItem>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableAutoFocus={true}
      disableRestoreFocus={true}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        pt: 8,
      }}
    >
      <Paper
        sx={{
          width: '66%',
          maxWidth: 800,
          maxHeight: '80vh',
          bgcolor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}
        onKeyDown={handleKeyDown}
      >
        {/* Search Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
            background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(220, 0, 78, 0.05) 100%)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SearchIcon sx={{ color: 'text.secondary', fontSize: 28 }} />
            <TextField
              ref={searchInputRef}
              fullWidth
              placeholder="Search diseases, treatments, ICD codes..."
              variant="standard"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontSize: 18,
                  fontWeight: 'medium',
                  '& input::placeholder': {
                    opacity: 0.7,
                  },
                },
              }}
            />
            <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Search Results */}
        <Box
          sx={{
            maxHeight: 'calc(80vh - 120px)',
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: 8,
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(0, 0, 0, 0.05)',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(0, 0, 0, 0.2)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(0, 0, 0, 0.3)',
            },
          }}
        >
          {!debouncedSearchTerm || debouncedSearchTerm.length < 2 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <SearchIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Start typing to search
              </Typography>
              <Typography variant="body2" color="text.disabled">
                Search for diseases, treatments, ICD codes, and more
              </Typography>
            </Box>
          ) : loading ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                Searching...
              </Typography>
            </Box>
          ) : results.length > 0 ? (
            <List sx={{ p: 0 }}>
              {results.map((result, index) => (
                <React.Fragment key={`${result.type}-${result.id}`}>
                  {renderResultItem(result, index)}
                  {index < results.length - 1 && (
                    <Divider sx={{ mx: 2, opacity: 0.5 }} />
                  )}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No results found
              </Typography>
              <Typography variant="body2" color="text.disabled">
                Try adjusting your search terms
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Modal>
  );
};

export default SearchModal;
